import React, { useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, message } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  BlockOutlined,
  HistoryOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  EditOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AppBreadcrumb from '../Common/AppBreadcrumb';
import LanguageSwitcher from '../Common/LanguageSwitcher';

const { Header, Sider, Content } = AntLayout;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(['document-management']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  // Menu items configuration
  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('navigation.dashboard'),
    },
    {
      key: 'document-management',
      icon: <FileTextOutlined />,
      label: t('navigation.documentManagement'),
      children: [
        // {
        //   key: '/review',
        //   icon: <EditOutlined />,
        //   label: t('navigation.documentReview'),
        // },
        {
          key: '/chunks',
          icon: <AppstoreOutlined />,
          label: t('navigation.chunkManagement'),
        },
        {
          key: '/azure-search',
          icon: <BlockOutlined />,
          label: t('navigation.azureSearch'),
        },
      ],
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: t('navigation.history'),
    },
    // {
    //   key: '/chat',
    //   icon: <MessageOutlined />,
    //   label: t('navigation.knowledgeChat'),
    // },
  ];

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('navigation.profile'),
      onClick: () => {
        message.info(t('settings.profile') + ' coming soon');
      },
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('navigation.settings'),
      onClick: () => {
        message.info(t('settings.title') + ' page coming soon');
      },
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.logout'),
      onClick: handleLogout,
    },
  ];

  async function handleLogout() {
    try {
      await logout();
      message.success(t('auth.logoutSuccess'));
      navigate('/login');
    } catch (error) {
      message.error(t('auth.logoutFailed'));
    }
  }

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  // Get current menu key from location and handle submenu selection
  const getCurrentMenuKeys = () => {
    const pathname = location.pathname === '/' ? '/dashboard' : location.pathname;
    
    // Define which routes belong to which parent menus
    const documentManagementRoutes = ['/review', '/chunks', '/azure-search'];
    
    if (documentManagementRoutes.includes(pathname)) {
      return {
        selectedKeys: [pathname],
        defaultOpenKeys: ['document-management']
      };
    }
    
    return {
      selectedKeys: [pathname],
      defaultOpenKeys: []
    };
  };

  const { selectedKeys, defaultOpenKeys } = getCurrentMenuKeys();

  // Handle submenu open/close
  const handleOpenChange = (keys: string[]) => {
    setMenuOpenKeys(keys);
  };

  // Set initial open keys based on current route
  React.useEffect(() => {
    if (defaultOpenKeys.length > 0 && !menuOpenKeys.includes('document-management')) {
      setMenuOpenKeys([...menuOpenKeys, ...defaultOpenKeys]);
    }
  }, [location.pathname]); // Re-run when route changes

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={280}
        collapsedWidth={80}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="logo">
          {collapsed ? 'RAG' : 'Navi Management'}
        </div>
        
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={menuOpenKeys}
          onOpenChange={handleOpenChange}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            borderRight: 0,
          }}
          defaultOpenKeys={['document-management']}
        />
      </Sider>

      <AntLayout style={{ marginLeft: collapsed ? 80 : 280, transition: 'all 0.2s' }}>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: '#fff', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'fixed',
            top: 0,
            right: 0,
            left: collapsed ? 80 : 280,
            zIndex: 1000,
            transition: 'all 0.2s'
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleCollapsed}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <LanguageSwitcher size="small" />
            <span>{t('common.welcome')}, {user?.full_name || user?.username}</span>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar 
                style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}
                icon={<UserOutlined />}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            margin: '24px',
            marginTop: '88px', // 64px header height + 24px margin
            background: '#f0f2f5',
            minHeight: 'calc(100vh - 112px)',
          }}
        >
          <div style={{ padding: '0 24px' }}>
            <AppBreadcrumb />
          </div>
          <div style={{ padding: '0 24px 24px 24px' }}>
            {children}
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
